import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { getReportContent, ReportsContentRequest } from "src/network/graphql/analyticsService";
import { ReportsContent } from "../reportsContent/reportsContentSlice";
import { convertToCSV } from "./licenceReportsContentConfig";
import { EMPTY_STRING } from "src/components/constant/constant";
import { organisationFilter, OrganisationFilterType } from "src/network/graphql/organisationService";

const initialReportsContent: ReportsContent[] = [];

export interface OrganisationType {
  organisation_id: string;
  name: string;
  display_id: string;
  type: string;
  abn: string;
}

const licenceReportsContent = createSlice({
  name: "licenceReportsContent",
  initialState: {
    loading: false,
    downloading: false,
    error: null,
    reportsContent: {
      list: initialReportsContent,
      size: 0
    },
    organisations: [] as OrganisationType[],
    searchingOrganisations: false
  },
  reducers: {
    fetchReportsContentStart: (state) => {
      state.loading = true;
    },
    fetchReportsContentSuccess: (state, action) => {
      state.loading = false;
      state.reportsContent = action.payload;
    },
    fetchReportsContentFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    downloadReportContentStart: (state) => {
      state.downloading = true;
    },
    downloadReportContentSuccess: (state) => {
      state.downloading = false;
    },
    downloadReportContentFail: (state, action) => {
      state.downloading = false;
      state.error = action.payload;
    },
    searchOrganisationsStart: (state) => {
      state.searchingOrganisations = true;
    },
    searchOrganisationsSuccess: (state, action) => {
      state.searchingOrganisations = false;
      state.organisations = action.payload;
    },
    searchOrganisationsFail: (state, action) => {
      state.searchingOrganisations = false;
      state.error = action.payload;
    }
  }
});

const {
  fetchReportsContentStart,
  fetchReportsContentSuccess,
  fetchReportsContentFail,
  downloadReportContentStart,
  downloadReportContentSuccess,
  downloadReportContentFail,
  searchOrganisationsStart,
  searchOrganisationsSuccess,
  searchOrganisationsFail
} = licenceReportsContent.actions;

export const fetchReportsContentAction = (data: ReportsContentRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchReportsContentStart());
    try {
      const response = await getReportContent(data);
      const structuredResponse = response.data.search_report_content.report_content_list.map((data: any) => ({
        object_id: data.object_id,
        object_type: data.object_type,
        report_date: data.report_date,
        report_type: data.report_type,
        data: JSON.parse(data.data)
      }));

      dispatch(
        fetchReportsContentSuccess({
          list: structuredResponse,
          size: response.data.search_report_content.total_size
        })
      );
    } catch (error) {
      dispatch(fetchReportsContentFail(error));
    }
  };
};

export const downloadReportAction = (data: ReportsContentRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(downloadReportContentStart());
    try {
      const response = await getReportContent(data);
      const structuredResponse = response.data.search_report_content.report_content_list.map((data: any) => ({
        object_id: data.object_id,
        object_type: data.object_type,
        report_date: data.report_date,
        report_type: data.report_type,
        data: JSON.parse(data.data)
      }));

      const csv = convertToCSV(structuredResponse);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.setAttribute("hidden", EMPTY_STRING);
      a.setAttribute("href", url);
      a.setAttribute("download", "licence_report_content.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      dispatch(downloadReportContentSuccess());
    } catch (error) {
      console.error("Error downloading report content:", error);
      dispatch(downloadReportContentFail(error));
    }
  };
};

export const searchOrganisationsAction = (searchName: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(searchOrganisationsStart());
    try {
      const data: OrganisationFilterType = {
        searchName,
        from: 0,
        limit: 20,
        active: true
      };
      const response = await organisationFilter(data);
      const organisations = response.data.search_organisation.organisation_listing.map((org: any) => ({
        organisation_id: org.organisation_id,
        name: org.name,
        display_id: org.display_id,
        type: org.type,
        abn: org.abn
      }));

      dispatch(searchOrganisationsSuccess(organisations));
    } catch (error) {
      console.error("Error searching organisations:", error);
      dispatch(searchOrganisationsFail(error));
    }
  };
};

export default licenceReportsContent.reducer;
